import React, { useState, useEffect } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, DEMO_USER } from '../../store/authStore';
import { authApi } from '../../api/client';
import { Printer, Layers, Warehouse, Truck, DollarSign, CheckCircle2, FlaskConical, KeyRound, Eye, EyeOff } from 'lucide-react';

const FEATURES = [
  { icon: Printer,      label: 'Gestión de Imprenta',  desc: 'Flujos de trabajo para trabajos de impresión' },
  { icon: Layers,       label: 'Producción DTF',        desc: 'Seguimiento de transferencias y materiales' },
  { icon: Warehouse,    label: 'Control de Stock',      desc: 'Alertas de inventario en tiempo real' },
  { icon: Truck,        label: 'Embalaje y Envío',      desc: 'Seguimiento de pedidos hasta la entrega' },
  { icon: DollarSign,   label: 'Módulo Financiero',     desc: 'Presupuestos, facturas y reportes' },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore(s => s.setAuth);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMasterLogin, setShowMasterLogin] = useState(false);
  const [masterEmail, setMasterEmail] = useState('');
  const [masterPassword, setMasterPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const handleSuccess = async (response: any) => {
    setError(null);
    setLoading(true);
    try {
      const data = await authApi.googleLogin(response.credential);
      setAuth(data.token, data.user);
      navigate('/');
    } catch (e: any) {
      setError(
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        'No se pudo iniciar sesión. Asegúrate de tener acceso autorizado.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleError = () => {
    setError('El inicio de sesión con Google fue cancelado o falló.');
  };

  const handleDemo = () => {
    setAuth('demo-token', DEMO_USER, true);
    navigate('/');
  };

  const handleMasterLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await authApi.login(masterEmail, masterPassword);
      setAuth(data.token, data.user);
      navigate('/');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Credenciales incorrectas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0a0f',
      display: 'flex',
      fontFamily: 'DM Sans, sans-serif',
    }}>
      {/* Left panel — branding (hidden on mobile) */}
      <div style={{
        flex: 1,
        display: isMobile ? 'none' : 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '60px 64px',
        background: 'linear-gradient(135deg, #0a0a0f 0%, #111018 60%, #140e0a 100%)',
        borderRight: '1px solid #1e1e2e',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Background glow */}
        <div style={{
          position: 'absolute', top: -200, left: -200,
          width: 600, height: 600,
          background: 'radial-gradient(circle, rgba(249,115,22,0.06) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: -100, right: -100,
          width: 400, height: 400,
          background: 'radial-gradient(circle, rgba(249,115,22,0.04) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 56 }}>
          <div style={{
            width: 48, height: 48,
            background: 'linear-gradient(135deg, #f97316, #ea580c)',
            borderRadius: 12,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'Syne, sans-serif', fontWeight: 900, fontSize: 22, color: '#fff',
            boxShadow: '0 0 32px rgba(249,115,22,0.4)',
          }}>P</div>
          <div>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 900, fontSize: 20, color: '#fff', letterSpacing: '-0.03em' }}>PRINT ERP</div>
            <div style={{ fontSize: 11, color: '#6b6880', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.12em' }}>v1.0 · SISTEMA DE GESTIÓN</div>
          </div>
        </div>

        {/* Headline */}
        <div style={{ marginBottom: 48 }}>
          <h1 style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 900,
            fontSize: 42,
            color: '#fff',
            margin: '0 0 16px',
            lineHeight: 1.1,
            letterSpacing: '-0.03em',
          }}>
            Gestión total de tu<br />
            <span style={{ color: '#f97316' }}>imprenta DTF</span>
          </h1>
          <p style={{ fontSize: 16, color: '#6b6880', margin: 0, lineHeight: 1.6, maxWidth: 420 }}>
            Controla proyectos, stock, producción y equipo desde un único sistema.
            Diseñado para equipos de impresión modernos.
          </p>
        </div>

        {/* Features grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {FEATURES.map(({ icon: Icon, label, desc }) => (
            <div key={label} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              padding: '12px 16px',
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid #1e1e2e',
              borderRadius: 10,
            }}>
              <div style={{
                width: 32, height: 32, flexShrink: 0,
                background: 'rgba(249,115,22,0.1)',
                borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon size={15} color="#f97316" />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#d1d0df', marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 11, color: '#4a4858', lineHeight: 1.4 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer note */}
        <div style={{ marginTop: 48, display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircle2 size={14} color="#22c55e" />
          <span style={{ fontSize: 12, color: '#4a4858' }}>Acceso seguro con Google Workspace</span>
        </div>
      </div>

      {/* Right panel — login card */}
      <div style={{
        width: isMobile ? '100%' : 480,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: isMobile ? '40px 24px' : '60px 48px',
        background: '#0a0a0f',
      }}>
        <div style={{
          width: '100%',
          maxWidth: 360,
          background: '#12121a',
          border: '1px solid #1e1e2e',
          borderRadius: 20,
          padding: '40px 36px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
        }}>
          {/* Card header */}
          <div style={{ marginBottom: 32, textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56,
              background: 'linear-gradient(135deg, #f97316, #ea580c)',
              borderRadius: 14,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
              boxShadow: '0 0 32px rgba(249,115,22,0.35)',
              fontFamily: 'Syne, sans-serif', fontWeight: 900, fontSize: 26, color: '#fff',
            }}>P</div>
            <h2 style={{
              fontFamily: 'Syne, sans-serif',
              fontWeight: 800,
              fontSize: 22,
              color: '#fff',
              margin: '0 0 8px',
              letterSpacing: '-0.02em',
            }}>Bienvenido de vuelta</h2>
            <p style={{ fontSize: 13, color: '#6b6880', margin: 0 }}>
              Inicia sesión con tu cuenta de Google
            </p>
          </div>

          {/* Divider */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28,
          }}>
            <div style={{ flex: 1, height: 1, background: '#1e1e2e' }} />
            <span style={{ fontSize: 11, color: '#4a4858', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em' }}>ACCESO AUTORIZADO</span>
            <div style={{ flex: 1, height: 1, background: '#1e1e2e' }} />
          </div>

          {/* Google login button */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            {loading ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 24px',
                background: '#1e1e2e',
                borderRadius: 10,
                fontSize: 14, color: '#6b6880',
              }}>
                <div style={{
                  width: 16, height: 16,
                  border: '2px solid #f97316',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }} />
                Autenticando...
              </div>
            ) : (
              <GoogleLogin
                onSuccess={handleSuccess}
                onError={handleError}
                theme="filled_black"
                shape="rectangular"
                size="large"
                text="signin_with"
              />
            )}
          </div>

          {/* Master login toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0 12px' }}>
            <div style={{ flex: 1, height: 1, background: '#1e1e2e' }} />
            <button
              onClick={() => { setShowMasterLogin(v => !v); setError(null); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 10, color: '#4a4858', fontFamily: 'JetBrains Mono, monospace',
                letterSpacing: '0.1em', padding: '2px 4px',
              }}
            >
              <KeyRound size={10} />
              ACCESO MAESTRO
            </button>
            <div style={{ flex: 1, height: 1, background: '#1e1e2e' }} />
          </div>

          {/* Master login form */}
          {showMasterLogin && (
            <form onSubmit={handleMasterLogin} style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 10 }}>
                <input
                  type="email"
                  placeholder="Email"
                  value={masterEmail}
                  onChange={e => setMasterEmail(e.target.value)}
                  required
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '10px 14px',
                    background: '#0a0a0f', border: '1px solid #1e1e2e',
                    borderRadius: 8, color: '#e2e1f0', fontSize: 13,
                    outline: 'none', fontFamily: 'DM Sans, sans-serif',
                  }}
                  onFocus={e => (e.target.style.borderColor = '#f97316')}
                  onBlur={e => (e.target.style.borderColor = '#1e1e2e')}
                />
              </div>
              <div style={{ marginBottom: 12, position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Contraseña"
                  value={masterPassword}
                  onChange={e => setMasterPassword(e.target.value)}
                  required
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '10px 40px 10px 14px',
                    background: '#0a0a0f', border: '1px solid #1e1e2e',
                    borderRadius: 8, color: '#e2e1f0', fontSize: 13,
                    outline: 'none', fontFamily: 'DM Sans, sans-serif',
                  }}
                  onFocus={e => (e.target.style.borderColor = '#f97316')}
                  onBlur={e => (e.target.style.borderColor = '#1e1e2e')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: '#4a4858', padding: 0,
                  }}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%', padding: '10px 16px',
                  background: 'linear-gradient(135deg, #f97316, #ea580c)',
                  border: 'none', borderRadius: 8,
                  color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? 'Accediendo...' : 'Entrar'}
              </button>
            </form>
          )}

          {/* Demo mode */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0 16px' }}>
            <div style={{ flex: 1, height: 1, background: '#1e1e2e' }} />
            <span style={{ fontSize: 10, color: '#2a2838', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em' }}>O</span>
            <div style={{ flex: 1, height: 1, background: '#1e1e2e' }} />
          </div>
          <button
            onClick={handleDemo}
            style={{
              width: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '10px 16px',
              background: 'rgba(249,115,22,0.08)',
              border: '1px dashed rgba(249,115,22,0.3)',
              borderRadius: 10,
              fontSize: 13, fontWeight: 500, color: '#f97316',
              cursor: 'pointer',
              transition: 'all 0.2s',
              marginBottom: 4,
            }}
            onMouseOver={e => (e.currentTarget.style.background = 'rgba(249,115,22,0.14)')}
            onMouseOut={e => (e.currentTarget.style.background = 'rgba(249,115,22,0.08)')}
          >
            <FlaskConical size={15} />
            Entrar en Modo Demo
          </button>

          {/* Error */}
          {error && (
            <div style={{
              padding: '10px 14px',
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 8,
              fontSize: 12,
              color: '#ef4444',
              marginBottom: 16,
              lineHeight: 1.5,
            }}>
              {error}
            </div>
          )}

          {/* Footer */}
          <p style={{
            fontSize: 11, color: '#4a4858', textAlign: 'center',
            margin: '24px 0 0', lineHeight: 1.5,
          }}>
            El acceso está restringido a usuarios autorizados.<br />
            Contacta con el administrador si necesitas acceso.
          </p>
        </div>

        {/* Bottom branding */}
        <div style={{ marginTop: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: '#2a2838', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em' }}>
            PRINT ERP · {new Date().getFullYear()}
          </div>
        </div>
      </div>

      {/* Spin animation */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
